import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { ReplaySubject, takeUntil } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { CTAService } from 'app/modules/social/services/cta/cta.service';
import { SocialFileService } from 'app/modules/social/services/social-file/social-file.service';
import { VideoChatService } from 'app/modules/social/services/videochat.service';
import { RoomSizeInfo } from 'app/modules/social/types/room';
import { Loggable } from 'app/utils/logging/loggable';
import CTAItem from 'entity/CTAItem';
import Playlist from 'entity/Playlist';
import SocialFile from 'entity/SocialFile';
import SocialTheme from 'entity/SocialTheme';
import CTAItemType from 'enums/CTAItemType';

import {
  ModalCreateItemComponent,
  ModalCreateItemDialogData,
  ModalCreateItemDialogResponse,
} from '../../components/modal-create-item/modal-create-item.component';
import {
  ModalUploadItemDialogData,
  ModalUploadFileComponent,
  UploadFile,
} from '../../components/modal-upload-file/modal-upload-file.component';
import { ScootaversePageService } from '../../services/scootaverse-page.service';
import { RoomSizes } from '../create-room/room-size-card/constants';

@Component({
  selector: 'edit-room-page',
  templateUrl: './edit-room-page.component.html',
  styleUrls: ['./edit-room-page.component.scss'],
  standalone: false,
})
export class EditRoomPageComponent extends Loggable implements OnInit, OnDestroy {
  private destroyed$ = new ReplaySubject<boolean>(1);

  protected filesArray: SocialFile[] = [];
  protected itemsArray: CTAItem[] = [];

  protected openRoomConfig = true;
  protected openRoomItems = true;
  protected openRoomFiles = true;
  protected showLoader = false;

  protected scootaverseId: string | undefined = undefined;
  protected roomId?: string;
  protected playlist?: Playlist;

  protected roomSize?: RoomSizeInfo;
  protected playlistId?: string;
  protected theme?: SocialTheme;
  protected roomSizesExamples = RoomSizes;
  protected selectedItem?: string;
  protected roomName?: string;

  private uploadedFiles: SocialFile[] = [];
  private ctaItem: CTAItem = {};

  public listItemSelectedId?: string;
  protected itemEditor = false;

  public constructor(
    private store: Store,
    private route: ActivatedRoute,
    private scootaversePageService: ScootaversePageService,
    private videoChatService: VideoChatService,
    private dialog: MatDialog,
    private socialFileService: SocialFileService,
    private _toasterService: ToastrService,
    private ctaService: CTAService,
  ) {
    super();
    this.setDisplayName('EditRoomPageComponent');
  }

  public ngOnInit(): void {
    this.scootaverseId = this.route.snapshot.paramMap.get('id') || undefined;
    if (!this.scootaverseId) {
      void this.scootaversePageService.redirectToHome();
    }
    this.roomId = this.route.snapshot.paramMap.get('room-id') || undefined;
    if (this.roomId) {
      this.showLoader = true;
      this.videoChatService
        .getSocialPromise(this.roomId, false)
        .then(async (social) => {
          this.roomSize = {
            capacity: social.maxParticipants?.toString() ?? '',
            size: social.startingHeight?.toString() ?? '',
            label: this.roomSizesExamples.find((room) => room.size === social.startingHeight)?.title,
          };

          this.playlistId = social.playlistId;
          this.theme = social.theme;
          this.roomName = social.name;

          this.playlist = await this.videoChatService.getPlaylistById(social.playlistId);

          await this.loadFiles();
          await this.loadCTAItems();
          this.showLoader = false;
        })
        .catch((err: unknown) => {
          this.error('Error loading social information: ', err);
          this.showLoader = false;
        });
    }

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.item_editor)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((itemEditor) => (this.itemEditor = itemEditor));
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public handleOpenRoomItems(): void {
    this.openRoomItems = !this.openRoomItems;
  }

  public handleOpenRoomFiles(): void {
    this.openRoomFiles = !this.openRoomFiles;
  }

  public handleOpenRoomConfig(): void {
    this.openRoomConfig = !this.openRoomConfig;
  }

  public formatSizLabel(label: string): string {
    return label ? `${label} room` : '';
  }

  public openUploadFileDialog(): void {
    const ref = this.dialog.open<ModalUploadFileComponent>(ModalUploadFileComponent, {
      width: '635px',
    });

    ref
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((fileData: UploadFile) => {
        if (fileData && this.scootaverseId) {
          this.showLoader = true;
          this.socialFileService
            .uploadFile(
              this.scootaverseId,
              fileData?.fileName,
              fileData?.fileType,
              fileData?.fileSize,
              fileData?.fileBytes,
            )
            .then(async (file: SocialFile) => {
              if (file) {
                this._toasterService.success('File upload successfully!', 'Success');
                await this.loadFiles();
                this.openRoomFiles = true;
                this.showLoader = false;
              }
            })
            .catch((err) => {
              const data: ModalUploadItemDialogData = {
                failedUpload: true,
                fileData: fileData,
              };

              this.dialog.open<ModalUploadFileComponent>(ModalUploadFileComponent, {
                data,
                width: '635px',
              });

              this.showLoader = false;

              this.error('Error uploading social file: ', err);
            });
        }
      });
  }

  private async loadFiles(): Promise<void> {
    if (this.scootaverseId) {
      this.filesArray = await this.socialFileService.getFiles(this.scootaverseId);
    }
  }

  private async loadCTAItems(): Promise<void> {
    if (this.roomId) {
      this.itemsArray = await this.ctaService.getCTAItems(this.roomId);
    }
  }

  public deleteFile(fileId?: string): void {
    if (this.scootaverseId && fileId) {
      this.showLoader = true;
      this.socialFileService
        .deleteFile(this.scootaverseId, fileId)
        .then(async () => {
          this._toasterService.success('File successfully deleted!', 'Success');
          await this.loadFiles();
          this.showLoader = false;
        })
        .catch((err) => {
          this._toasterService.error('Error deleting social file!');
          this.error('Error deleting social file: ', err);
          this.showLoader = false;
        });
    }
  }

  public openItemCreatorModal(ctaItemEdit?: CTAItem): void {
    const data: ModalCreateItemDialogData = {
      scootaverseId: this.scootaverseId ?? '',
      ctaItem: ctaItemEdit,
    };

    const ref = this.dialog.open<ModalCreateItemComponent>(ModalCreateItemComponent, {
      data,
      width: '880px',
    });

    ref
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((item: ModalCreateItemDialogResponse) => {
        if (item && this.roomId) {
          this.showLoader = true;
          if (item.contentInfo?.uploadedFiles) {
            item.contentInfo?.uploadedFiles.forEach((file) =>
              this.uploadFile(file.fileName, file.fileType, file.fileSize, file.fileBytes),
            );
          }

          this.ctaItem = {
            name: item.itemName,
            imageBytes: item.itemImage?.imageBytes,
            buttonVisible: item.hasButton,
            traversable: item.itemIsTraversable,
            imageHeight: item.itemImage?.imageHeight,
            imageWidth: item.itemImage?.imageWidth,
          };

          if (item.isEdit) {
            this.ctaItem = {
              ...this.ctaItem,
              CTAItemId: item.CTAItemId,
              x: ctaItemEdit?.x,
              y: ctaItemEdit?.y,
            };
          }

          if (item.hasButton) {
            this.ctaItem = {
              ...this.ctaItem,
              buttonColor: item.buttonInfo?.buttonColor,
              buttonTextColor: item.buttonInfo?.buttonTextColor,
              text: item.buttonInfo?.buttonText,
              buttonSize: item.buttonInfo?.buttonSize,
            };
          }

          if (item.contentInfo?.urlLink) {
            this.ctaItem = {
              ...this.ctaItem,
              url: item.contentInfo?.urlLink,
              type: CTAItemType.URL,
            };
          } else {
            this.ctaItem = {
              ...this.ctaItem,
              type: CTAItemType.DOWNLOAD,
            };
          }

          if (item.isEdit) {
            this.ctaService
              .updateCTA(this.ctaItem)
              .then(async (ctaItemResp: CTAItem) => {
                if (item.hasContent && (item.contentInfo?.selectedFiles || item.contentInfo?.uploadedFiles)) {
                  const filesId: string[] = [];
                  const removeFiles: string[] = [];

                  if (item.filesArray && item.contentInfo?.selectedFiles) {
                    const resp = this.getFilesToRemove(item.filesArray, item.contentInfo?.selectedFiles);
                    resp.forEach((file) => removeFiles.push(file.socialFileId ?? ''));
                  }

                  if (item.contentInfo?.selectedFiles) {
                    item.contentInfo.selectedFiles.forEach((file) => filesId.push(file.socialFileId ?? ''));
                  }

                  if (item.contentInfo.uploadedFiles) {
                    this.uploadedFiles.forEach((file) => filesId.push(file.socialFileId ?? ''));
                  }

                  if (ctaItemResp.CTAItemId && filesId) {
                    this.addFilesToCTAItem(ctaItemResp.CTAItemId, filesId);
                  }

                  if (ctaItemResp.CTAItemId && removeFiles) {
                    this.removeFilesFromCTAItem(ctaItemResp.CTAItemId, removeFiles);
                  }
                }

                this._toasterService.success('CTA item successfully updated!', 'Success');
                await this.loadCTAItems();
                await this.loadFiles();

                this.showLoader = false;
              })
              .catch((err) => {
                this._toasterService.error('Error updating CTA item!');
                this.error('Error updating CTA item: ', err);
                this.showLoader = false;
              });
          } else {
            this.ctaService
              .createCTA(this.roomId, this.ctaItem)
              .then(async (ctaItemResp: CTAItem) => {
                if (item.hasContent && (item.contentInfo?.selectedFiles || item.contentInfo?.uploadedFiles)) {
                  const filesId: string[] = [];
                  if (item.contentInfo?.selectedFiles) {
                    item.contentInfo.selectedFiles.forEach((file) => filesId.push(file.socialFileId ?? ''));
                  }
                  if (item.contentInfo.uploadedFiles) {
                    this.uploadedFiles.forEach((file) => filesId.push(file.socialFileId ?? ''));
                  }

                  if (ctaItemResp.CTAItemId) {
                    this.addFilesToCTAItem(ctaItemResp.CTAItemId, filesId);
                  }
                }

                this._toasterService.success('CTA item successfully created!', 'Success');
                await this.loadCTAItems();
                await this.loadFiles();
                this.openRoomItems = true;
                this.showLoader = false;
              })
              .catch((err) => {
                this._toasterService.error('Error creating CTA item!');
                this.error('Error creating CTA item: ', err);
                this.showLoader = false;
              });
          }
        }
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private uploadFile(fileName: string, fileType: string, fileSize: number, fileBytes: any): void {
    if (this.scootaverseId) {
      this.showLoader = true;
      this.socialFileService
        .uploadFile(this.scootaverseId, fileName, fileType, fileSize, fileBytes)
        .then((file: SocialFile) => {
          if (file) {
            this.uploadedFiles.push(file);
            this.showLoader = false;
          }
        })
        .catch((err) => {
          this.showLoader = false;
          this.error('Error uploading social file: ', err);
        });
    }
  }

  private addFilesToCTAItem(ctaItemId: number, filesId: string[]): void {
    this.socialFileService.addCTAItemFiles(ctaItemId, filesId).catch((err) => {
      this.showLoader = false;
      this.error('Error adding social file to CTA item: ', err);
    });
  }

  private removeFilesFromCTAItem(ctaItemId: number, filesId: string[]): void {
    this.socialFileService.removeCTAItemFiles(ctaItemId, filesId).catch((err) => {
      this.showLoader = false;
      this.error('Error removing social file from CTA item: ', err);
    });
  }

  public async redirectToEditStep(step: string): Promise<void> {
    if (this.roomId && this.scootaverseId) {
      await this.scootaversePageService.editRoomStep(this.roomId, this.scootaverseId, step);
    }
  }

  public onEditItem(item: CTAItem): void {
    this.openItemCreatorModal(item);
  }

  public onDuplicateItem(item: CTAItem): void {
    if (item.CTAItemId && item.name) {
      this.showLoader = true;
      const copyName = `Copy of ${item.name}`;
      this.ctaService
        .duplicateCTA(item.CTAItemId, copyName)
        .then(async (copyItem) => {
          if (copyItem) {
            this._toasterService.success('Item successfully duplicated!', 'Success');
            await this.loadCTAItems();
            this.showLoader = false;
          }
        })
        .catch((err) => {
          this.error('Error duplicating social file: ', err);
          this._toasterService.error('Error duplicating social file.');
          this.showLoader = false;
        });
    }
  }

  public onDeleteItem(item: CTAItem): void {
    if (item.CTAItemId) {
      this.showLoader = true;
      this.ctaService
        .deleteCTA(item.CTAItemId)
        .then(async () => {
          this._toasterService.success('Item successfully deleted!', 'Success');
          await this.loadCTAItems();
          this.showLoader = false;
        })
        .catch((err) => {
          this.error('Error deleting social file: ', err);
          this._toasterService.error('Error deleting social file.');
          this.showLoader = false;
        });
    }
  }

  public async redirectToRoomSettings(): Promise<void> {
    if (this.scootaverseId) {
      await this.scootaversePageService.onScootaverseSettings(this.scootaverseId, false);
    }
  }

  public getFilesToRemove(filesArray: SocialFile[], selectedFiles: SocialFile[]): SocialFile[] {
    const diffFromA1toA2 = filesArray.filter((obj1) => {
      if (selectedFiles) {
        return !selectedFiles.some((obj2) => obj1.socialFileId === obj2.socialFileId);
      }
    });

    const diffFromA2toA1 = selectedFiles.filter((obj2) => {
      if (filesArray) {
        return !filesArray.some((obj1) => obj2.socialFileId === obj1.socialFileId);
      }
    });

    return [...diffFromA1toA2, ...diffFromA2toA1];
  }

  public selectCTAItem(event: string): void {
    this.selectedItem = event;
  }

  public checkItemSelected(itemId?: number): boolean {
    return itemId ? this.selectedItem === itemId.toString() : false;
  }

  public get addOverflowItems(): boolean {
    return this.itemsArray.length > 5;
  }

  public get addOverflowFiles(): boolean {
    return this.filesArray.length > 5;
  }

  public selectCTAItemList(itemId?: number): void {
    if (itemId) {
      this.listItemSelectedId = itemId.toString();
      this.selectCTAItem(itemId.toString());
    }
  }
}
